/**
 * HeatingCostTable — Fuel cost comparison table
 *
 * Displays all fuel variants sorted by annual cost.
 * Expandable rows show technical details (calorific value, efficiency).
 */

import { useState } from 'react';
import {
  Table,
  Text,
  Badge,
  ActionIcon,
  Collapse,
  Group,
  Stack,
  Tooltip,
} from '@mantine/core';
import type { FuelCostResult, HeatingCostComparison } from '../types/heatingCost.types';
import { FUEL_CATEGORY_COLORS } from '../constants/fuelDefaults';

interface HeatingCostTableProps {
  comparison: HeatingCostComparison;
  onEditPrice?: (variantId: string) => void;
}

function formatCurrency(value: number): string {
  return value.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatNumber(value: number, decimals = 2): string {
  return value.toLocaleString('pl-PL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function ExpandedDetails({ result }: { result: FuelCostResult }) {
  const { variant, price } = result;
  return (
    <Stack gap="xs" p="sm" bg="gray.0">
      <Group gap="lg" wrap="wrap">
        <Text size="xs" c="dimmed">
          Wartość opałowa: <strong>{variant.calorificValue} {variant.calorificUnit}</strong>
        </Text>
        <Text size="xs" c="dimmed">
          Sprawność: <strong>{variant.efficiencyUnit === 'COP/SPF'
            ? `COP ${variant.efficiency}`
            : `${(variant.efficiency * 100).toFixed(0)}%`}</strong>
        </Text>
        <Text size="xs" c="dimmed">
          Cena: <strong>{price.pricePerUnit} {price.priceUnit}</strong>
          {price.isUserModified && <Badge size="xs" ml={4} color="orange" variant="light">edytowana</Badge>}
        </Text>
        <Text size="xs" c="dimmed">
          Zużycie: <strong>{formatNumber(result.annualConsumption)} {variant.consumptionUnit}</strong>
        </Text>
        <Text size="xs" c="dimmed">
          Koszt netto: <strong>{formatCurrency(result.annualCostNet)} zł/rok</strong>
        </Text>
      </Group>
    </Stack>
  );
}

export function HeatingCostTable({ comparison, onEditPrice }: HeatingCostTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  return (
    <Table striped highlightOnHover withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th style={{ width: 40 }}>#</Table.Th>
          <Table.Th>Źródło ciepła</Table.Th>
          <Table.Th style={{ textAlign: 'right' }}>Koszt roczny</Table.Th>
          <Table.Th style={{ textAlign: 'right' }}>Koszt/kWh</Table.Th>
          <Table.Th style={{ width: 40 }}></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {comparison.results.map((result, index) => {
          const isExpanded = expandedId === result.variant.id;
          const categoryColor = FUEL_CATEGORY_COLORS[result.variant.category] ?? '#999';

          return (
            <Table.Tr
              key={result.variant.id}
              onClick={() => toggleExpand(result.variant.id)}
              style={{ cursor: 'pointer' }}
            >
              <Table.Td>
                <Text size="sm" fw={500} c="dimmed">{index + 1}</Text>
              </Table.Td>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <div style={{
                    width: 4,
                    height: 28,
                    borderRadius: 2,
                    backgroundColor: categoryColor,
                    flexShrink: 0,
                  }} />
                  <Stack gap={2}>
                    <Group gap={4}>
                      <Text size="sm" fw={500}>
                        {result.variant.label}
                      </Text>
                      {result.isCurrentSystem && (
                        <Badge size="xs" color="blue" variant="light">obecny</Badge>
                      )}
                      {result.isCheapest && (
                        <Badge size="xs" color="green" variant="light">najtańszy</Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">{result.variant.boilerType}</Text>
                  </Stack>
                </Group>
                <Collapse in={isExpanded}>
                  <ExpandedDetails result={result} />
                </Collapse>
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Text size="sm" fw={result.isCheapest ? 700 : 400}
                  c={result.isCheapest ? 'green' : undefined}>
                  {formatCurrency(result.annualCostGross)} zł
                </Text>
              </Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>
                <Text size="sm">{formatNumber(result.costPerKwh, 2)} zł</Text>
              </Table.Td>
              <Table.Td>
                {onEditPrice && (
                  <Tooltip label="Edytuj cenę">
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditPrice(result.variant.id);
                      }}
                    >
                      <Text size="xs">✎</Text>
                    </ActionIcon>
                  </Tooltip>
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
